import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "./ConfirmDialog";
import { toast } from "sonner";
import { SignOut, Key, User as UserIcon, CaretDown } from "@phosphor-icons/react";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase() || "U";
}

const ROLE_TONE = {
  admin: "bg-orange-500 text-black",
  production: "bg-blue-500 text-white",
  accounting: "bg-green-500 text-white",
  sales: "bg-purple-500 text-white",
};

export default function UserProfileChip() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { confirm } = useConfirm();
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;
  const toneCls = ROLE_TONE[user.role] || "bg-zinc-700 text-white";

  const onSignOut = async () => {
    setOpen(false);
    const ok = await confirm({
      title: "Sign out?",
      message: `You'll return to the landing page. Any unsaved work will be lost.`,
      tone: "warning",
      confirmLabel: "Sign out",
    });
    if (!ok) return;
    logout();
    nav("/");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 pl-1 pr-2 py-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors rounded-full"
        data-testid="profile-chip"
        title={`${user.name} · ${user.role}`}
      >
        <div className={`w-8 h-8 rounded-full ${toneCls} flex items-center justify-center font-black text-xs shadow-md ring-2 ring-white/30`}>{initials(user.name)}</div>
        <div className="text-left pr-1">
          <div className="font-bold text-xs leading-none text-white truncate max-w-[110px]">{user.name?.split(" ")[0] || user.email}</div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/70 mt-0.5">{user.role}</div>
        </div>
        <CaretDown size={12} weight="bold" className={`text-white/80 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-neutral-900 border border-zinc-200 dark:border-neutral-800 shadow-xl z-50 text-zinc-900 dark:text-white" data-testid="profile-dropdown">
          <div className="p-4 border-b border-zinc-200">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-full ${toneCls} flex items-center justify-center font-black text-sm`}>{initials(user.name)}</div>
              <div className="min-w-0">
                <div className="font-black text-sm truncate">{user.name}</div>
                <div className="text-[11px] text-zinc-500 truncate font-mono">{user.email}</div>
              </div>
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {user.permissions?.length || 0} screens · {user.role} role
            </div>
          </div>
          <button
            onClick={() => { setOpen(false); setShowProfile(true); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 text-sm text-left"
            data-testid="profile-menu-view"
          >
            <UserIcon size={16} weight="bold" className="text-zinc-500" /> My Profile
          </button>
          <button
            onClick={() => { setOpen(false); setShowPwd(true); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50 text-sm text-left"
            data-testid="profile-menu-pwd"
          >
            <Key size={16} weight="bold" className="text-zinc-500" /> Change Password
          </button>
          <div className="border-t border-zinc-200" />
          <button
            onClick={onSignOut}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 hover:text-red-600 text-sm text-left"
            data-testid="profile-menu-signout"
          >
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      )}

      {showProfile && <ProfileModal user={user} onClose={() => setShowProfile(false)} />}
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
}

function ProfileModal({ user, onClose }) {
  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="modal-overlay">
      <div className="bg-white shadow-2xl w-full sm:max-w-md border-t-4 border-orange-500 flex flex-col h-screen sm:h-auto sm:max-h-[90vh]" onClick={e => e.stopPropagation()} data-testid="profile-modal">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-zinc-200 shrink-0">
          <h3 className="text-lg sm:text-xl font-black tracking-tight">My Profile</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-100 transition-colors" aria-label="Close">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <dl className="space-y-3 text-sm">
            <Row label="Name" value={user.name} />
            <Row label="Email" value={user.email} />
            <Row label="Role" value={user.role} />
            <Row label="Active" value={user.active ? "Yes" : "No"} />
            <Row label="Screens" value={(user.permissions || []).join(", ") || "—"} />
          </dl>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 bg-zinc-50 border-t border-zinc-200 shrink-0">
          <button onClick={onClose} className="px-4 py-2 bg-zinc-900 hover:bg-orange-600 text-white text-sm font-bold uppercase tracking-widest transition-colors">Close</button>
        </div>
      </div>
    </div>
  ), document.body);
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 pt-1">{label}</dt>
      <dd className="col-span-2 font-medium break-words">{value}</dd>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (n1.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (n1 !== n2) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: n1 });
      toast.success("Password changed");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally { setBusy(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="modal-overlay">
      <form onSubmit={submit} className="bg-white shadow-2xl w-full sm:max-w-md border-t-4 border-orange-500 flex flex-col h-screen sm:h-auto sm:max-h-[90vh]" onClick={e => e.stopPropagation()} data-testid="pwd-modal">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-zinc-200 shrink-0">
          <h3 className="text-lg sm:text-xl font-black tracking-tight">Change Password</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-100 transition-colors" aria-label="Close">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Current Password</label>
              <input type="password" value={cur} onChange={e => setCur(e.target.value)} required autoFocus className="w-full border-2 border-zinc-300 focus:border-orange-500 focus:outline-none px-3 py-2" data-testid="pwd-current" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">New Password (min 6 chars)</label>
              <input type="password" value={n1} onChange={e => setN1(e.target.value)} required className="w-full border-2 border-zinc-300 focus:border-orange-500 focus:outline-none px-3 py-2" data-testid="pwd-new" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Confirm New Password</label>
              <input type="password" value={n2} onChange={e => setN2(e.target.value)} required className="w-full border-2 border-zinc-300 focus:border-orange-500 focus:outline-none px-3 py-2" data-testid="pwd-confirm" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 bg-zinc-50 border-t border-zinc-200 shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-zinc-300 hover:bg-zinc-100 text-sm font-bold uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold uppercase tracking-widest disabled:opacity-50" data-testid="pwd-save">{busy ? "Saving…" : "Change Password"}</button>
        </div>
      </form>
    </div>
  ), document.body);
}
